import { cache } from "react";

import InterfaceStore, { type IInterfaceStoreInitData } from "./InterfaceStore";
import I18nStore, { type II18nStoreInitData } from "./i18nStore";
import OrganizationStore, {
  type IOrganizationStoreInitData,
} from "./OrganizationStore";
import UserStore, { type IUserStoreInitData } from "./UserStore";
import ModalStore from "./ModalStore";
import StyleStore from "./StyleStore";
import GridStore from "./GridStore";
import SidebarStore, { type ISidebarStoreInitData } from "./SidebarStore";
import { toJS } from "mobx";

// load this file only once!

const organizationStore = new OrganizationStore(undefined);
const interfaceStore = new InterfaceStore(undefined);
const userStore = new UserStore(undefined);
const modalStore = new ModalStore(undefined);
const styleStore = new StyleStore(undefined, organizationStore, interfaceStore);
const gridStore = new GridStore(undefined);
const i18nStore = new I18nStore(undefined, organizationStore);
const sidebarStore = new SidebarStore(undefined);

const allStores = {
  interfaceStore,
  organizationStore,
  i18nStore,
  modalStore,
  userStore,
  styleStore,
  gridStore,
  sidebarStore,
};

function deriveNewStoreInitData(store, initData) {
  if (store.constructor.name === "OrganizationStore") {
    const preserveTitleOrganizationFields = [
      "locations",
      "navigation",
      "categories",
      "media",
      "url",
      "district",
      "billing_products",
      "user_roles",
    ];
    const originalStoreData = toJS(store.serialize());
    const derivedStoreData = Object.assign({ ...originalStoreData }, initData);
    preserveTitleOrganizationFields.forEach((field) => {
      derivedStoreData.organization[field] =
        originalStoreData.organization[field];
    });
    return derivedStoreData;
  } else {
    return Object.assign(toJS(store.serialize()), initData);
  }
}

export function rebuildStoresFromInitData(initData) {
  const interfaceStoreInitData = deriveNewStoreInitData(
    allStores.interfaceStore,
    initData,
  );
  allStores.interfaceStore = new InterfaceStore(
    interfaceStoreInitData as IInterfaceStoreInitData,
  );

  const organizationStoreInitData = deriveNewStoreInitData(
    allStores.organizationStore,
    initData,
  );
  allStores.organizationStore = new OrganizationStore(
    organizationStoreInitData as IOrganizationStoreInitData,
  );

  const i18nStoreInitData = deriveNewStoreInitData(
    allStores.i18nStore,
    initData,
  );
  allStores.i18nStore = new I18nStore(
    i18nStoreInitData as II18nStoreInitData,
    allStores.organizationStore,
  );

  const styleStoreInitData = deriveNewStoreInitData(
    allStores.styleStore,
    initData,
  );
  allStores.styleStore = new StyleStore(
    styleStoreInitData,
    allStores.organizationStore,
    allStores.interfaceStore,
  );

  const gridStoreInitData = deriveNewStoreInitData(
    allStores.gridStore,
    initData,
  );
  allStores.gridStore = new GridStore(gridStoreInitData);

  const sidebarStoreInitData = deriveNewStoreInitData(
    allStores.sidebarStore,
    initData,
  );
  allStores.sidebarStore = new SidebarStore(
    sidebarStoreInitData as ISidebarStoreInitData,
  );

  const modalStoreInitData = deriveNewStoreInitData(
    allStores.modalStore,
    initData,
  );
  allStores.modalStore = new ModalStore(modalStoreInitData);

  const userStoreInitData = deriveNewStoreInitData(
    allStores.userStore,
    initData,
  );
  allStores.userStore = new UserStore(userStoreInitData as IUserStoreInitData);

  return allStores;
}

export interface AppContextType {
  interfaceStore: any;
  organizationStore: any;
  i18nStore: I18nStore;
  modalStore: ModalStore;
  userStore: any;
  styleStore: any;
  gridStore: any;
  sidebarStore: any;
}

export default allStores;

function _loadStores(initialLookupData) {
  const interfaceStore = new InterfaceStore(initialLookupData);
  const userStore = new UserStore(initialLookupData);
  const organizationStore: any = new OrganizationStore(initialLookupData);
  const modalStore = new ModalStore(initialLookupData);
  const styleStore = new StyleStore(
    initialLookupData,
    organizationStore,
    interfaceStore,
  );
  const gridStore = new GridStore(initialLookupData);
  const i18nStore = new I18nStore(initialLookupData, organizationStore);
  const sidebarStore = new SidebarStore(initialLookupData);

  return {
    interfaceStore,
    organizationStore,
    modalStore,
    userStore,
    styleStore,
    gridStore,
    i18nStore,
    sidebarStore,
  } as any;
}

export const loadStores = cache(_loadStores);
